/**
 * REK — Basisrekenen rulebook.
 *
 * Sourced from DEFAULT_POLICY (Basisrekenen entries) +
 * docs/audits/DIDACTISCH_AUDIT_REK_2026.md +
 * docs/audits/REK_SEEDS_DIDACTIC_AUDIT_2026.md +
 * MODULE_AUTHORING_GUIDE §2.
 */

import type { ModuleRulebook } from './types'

export const REK_RULEBOOK: ModuleRulebook = {
  moduleId: 'Basisrekenen',
  shortCode: 'REK',
  displayName: 'Basisrekenen',

  didactics: {
    principles: [
      'strategie_eerst',
      'cijferend_vs_handig',
      'tafels_recall',
      'context_herkenning',
    ],
    schoolbookReferences: ['Wereld in Getallen', 'Pluspunt', 'Reken Zeker'],
    misconceptionDomain: 'rek',
    coreSummary:
      'Beheersing van basisbewerkingen + bewust kiezen van strategie (cijferend vs. handig) bij contextopgaven.',
  },

  notation: {
    requireCommaDecimals: true,
    forbiddenPatterns: [
      {
        pattern: '\\d+\\.\\d+',
        label: 'Punt als decimaalteken (gebruik komma: 0,5)',
      },
    ],
  },

  content: {
    distractorMisconceptionPattern:
      'tafels|cijferend|optellen|aftrekken|vermenigvuldigen|delen|handig|schatten|volgorde|splitsen|compenseren|rijgen|lenen|onthouden|afronden|magnitude|grootte-orde|naar boven|naar beneden',
  },

  explanation: {
    requireRuleReference: true,
    // Vanaf tier 9 (groep 7) wordt strategiekeuze bewust gemaakt — leerlingen
    // moeten dan kunnen verantwoorden WAAROM ze handig of cijferend kiezen.
    checkStrategyMinTier: 9,
    preferredStrategyKeywords: [
      'splitsen',
      'compenseren',
      'rijgen',
      'verdubbelen',
      'halveren',
      'aanvullen',
    ],
  },

  visuals: {
    preferredAidTypes: [
      'column',
      'numberline',
      'numberLineLeaps',
      'compensationNumberLine',
      'splitDiagram',
      'strategySteps',
      'numberBoard',
      'contextSpotlight',
      'barModel',
    ],
    familyVisualHints: [
      // — Cijferend rekenen (kolomopzet primair) —
      {
        familyIdPattern: '^REK-CIJF-',
        preferredStimulus: 'column',
        note: 'Cijferend rekenen vraagt om een kolom-visual met tussenresultaten.',
      },

      // — Handig rekenen plus/min: strategiekeuze-as splitsen vs compenseren —
      // strategySteps toont de keten letterlijk; splitDiagram visualiseert
      // splitsen; compensationNumberLine visualiseert compenseren rond een
      // rond getal; numberLineLeaps voor rijgen-sprongen.
      {
        familyIdPattern: '^REK-HANDIG-PLUS-MIN',
        preferredSupport: 'strategySteps',
        stimulusAllowed: [
          'splitDiagram',
          'compensationNumberLine',
          'numberLineLeaps',
          'numberline',
          'strategySteps',
        ],
        note: 'Handig plus/min: strategySteps-kaart als default-support; splitDiagram (splitsen), compensationNumberLine (compenseren) of numberLineLeaps (rijgen) als stimulus, afhankelijk van de strategie die de vraag uitlokt.',
      },

      // — Handig keer: distributief splitsen, verdubbelen/halveren —
      // Strategy is the universal frame for handig keer: distributief
      // (splitDiagram), verdubbel/halveer (strategySteps), ×9-compenseren
      // (compensationNumberLine) en sprongen (numberLineLeaps) zijn allemaal
      // varianten van dezelfde strategiekeuze-as.
      {
        familyIdPattern: '^REK-HANDIG-KEER',
        preferredSupport: 'strategySteps',
        stimulusAllowed: [
          'splitDiagram',
          'numberLineLeaps',
          'compensationNumberLine',
          'strategySteps',
          'numberline',
        ],
        autoExemptEmbedded: true,
        note: 'Handig keer: strategiekeuze is leidend — strategySteps default. splitDiagram bij distributief (8×27 = 8×25 + 8×2), numberLineLeaps bij herhaald-optellen-sprongen, compensationNumberLine bij ×9 / ×99 (compenseren via tiental). autoExemptEmbedded dekt number_sequence en strategy_solver.',
      },

      // — Handig deel: halveren, herhaald aftrekken, splitsen —
      {
        familyIdPattern: '^REK-HANDIG-DEEL',
        preferredSupport: 'splitDiagram',
        stimulusAllowed: [
          'splitDiagram',
          'numberLineLeaps',
          'strategySteps',
          'numberline',
        ],
        autoExemptEmbedded: true,
        note: 'Handig deel: splitsen van deeltal (96÷4 = (80+16)÷4) — splitDiagram default. numberLineLeaps voor herhaald-aftrekken, strategySteps voor halveren-keten. autoExemptEmbedded dekt number_sequence.',
      },

      // — Handig (catch-all voor toekomstige sub-families) —
      {
        familyIdPattern: '^REK-HANDIG-',
        preferredSupport: 'strategySteps',
        note: 'Handig rekenen (overig): strategy-as-steps; specifieke sub-families hebben preciezere hints hierboven.',
      },

      // — Contextopgaven meerstaps (strategy_solver/step_solver — strategie-keten) —
      // Meerstaps-redenering ≠ verdeel-/verhoudings-strookmodel: bij meerstaps
      // moet het kind eerst de DEELBEWERKINGEN identificeren en ORDENEN
      // (lees → bewerking-1 → bewerking-2 → tegenproef). strategySteps maakt die
      // keten zichtbaar; barModel verbergt juist de keten in één strook.
      {
        familyIdPattern: '^REK-CONTEXT-MEERSTAPS',
        preferredSupport: 'strategySteps',
        stimulusAllowed: ['strategySteps', 'barModel', 'rule'],
        autoExemptEmbedded: true,
        note: 'Meerstaps-context: strategieketen (bewerking-1 → bewerking-2 → tegenproef) — strategySteps default. barModel blijft toegestaan voor verdeel-sub-stappen; rule-kaart voor de strategie-uitleg. autoExemptEmbedded dekt step_solver / strategy_solver.',
      },

      // — Contextopgaven (strookmodel) —
      {
        familyIdPattern: '^REK-CONTEXT-',
        preferredSupport: 'barModel',
        autoExemptEmbedded: true,
        note: 'Singapore-strookmodel maakt verdeel-/verhoudings-relaties in contextsommen zichtbaar. autoExemptEmbedded dekt bar_model_part / step_solver / strategy_solver (eigen visueel oppervlak).',
      },

      // — Tafels basis (recall — geen verplichte stimulus) —
      {
        familyIdPattern: '^REK-TAFELS-BASIS',
        preferredSupport: 'numberBoard',
        note: 'Tafels-basis is recall — geen verplichte stimulus, numberBoard als optionele oefen-support.',
      },

      // — Tafels uitbreiden / toepassen: distributief splitsen —
      // Distributieve uitbreiding (8×27 via 8×25+8×2) past beter bij splitDiagram
      // of barModel dan bij de generieke numberline. numberLineLeaps blijft
      // bruikbaar voor sprongen-georiënteerde varianten.
      {
        familyIdPattern: '^REK-TAFELS-UITBREIDEN',
        preferredSupport: 'splitDiagram',
        stimulusAllowed: [
          'splitDiagram',
          'barModel',
          'numberLineLeaps',
          'numberline',
        ],
        note: 'Tafels uitbreiden: distributief splitsen — splitDiagram of barModel maken (b+c)×a = b×a + c×a zichtbaar. numberLineLeaps voor sprongen-variant.',
      },
      {
        familyIdPattern: '^REK-TAFELS-TOEPASSEN',
        preferredSupport: 'numberLineLeaps',
        stimulusAllowed: ['numberLineLeaps', 'numberline', 'splitDiagram'],
        note: 'Tafels toepassen: numberLineLeaps visualiseert herhaald-optellen-sprongen van X; splitDiagram als context distributief is.',
      },

      // — Schatten (getallenlijn met ankerpunten) —
      {
        familyIdPattern: '^REK-SCHATTEN',
        preferredStimulus: 'numberline',
        note: 'Schatten: getallenlijn met buurankers maakt "tussen welke ronde getallen?" zichtbaar.',
      },

      // — Volgorde der bewerkingen (regel-kaart) —
      {
        familyIdPattern: '^REK-VOLGORDE',
        preferredSupport: 'rule',
        roleScope: 'rule-only',
        note: 'Voorrangsregels (× en ÷ vóór + en −, haakjes eerst): regel-kaart als geheugensteun.',
      },

      // — Gemengde toetsen (geen vaste visual; afhankelijk van bewerking) —
      {
        familyIdPattern: '^REK-GEMENGD-',
        autoExemptEmbedded: true,
        note: 'Gemengde sets: visual volgt per vraag de bewerking (kolom voor cijferend, numberline voor handig).',
      },
    ],
  },

  questionTypes: {
    preferredTypes: [
      'multiple_choice',
      'fill_in',
      'cijferend_rekenen',
      'cijferend_delen',
      'step_solver',
      'strategy_solver',
      'multiplication_grid',
      'bar_model_part',
      'number_sequence',
      'true_false_series',
      'error_spotting',
      'estimation_slider',
      'ordering',
    ],
    discouragedTypes: [
      {
        type: 'text_highlight',
        reason: 'Niet geschikt voor numerieke bewerkingen.',
      },
    ],
    customTypeRoles: [
      {
        type: 'bar_model_part',
        didacticRole: 'strategic',
        preferredDok: 2,
        appropriateTiers: { min: 5, max: 14 },
        reason:
          'Strookmodel bij contextopgaven — kind leest een deel af en herkent zo de bewerking voordat hij rekent.',
      },
      {
        type: 'cijferend_rekenen',
        didacticRole: 'fluency',
        preferredDok: 2,
        appropriateTiers: { min: 5, max: 14 },
        reason:
          'Kolomopzet voor +/−/×: tussenresultaten + lenen/onthouden expliciet — fluency-georiënteerd.',
      },
      {
        type: 'cijferend_delen',
        didacticRole: 'fluency',
        preferredDok: 2,
        appropriateTiers: { min: 7, max: 14 },
        reason:
          'Staartdeling stap-voor-stap: visueel zichtbare deelstappen — fluency vanaf groep 6.',
      },
      {
        type: 'multiplication_grid',
        didacticRole: 'concept',
        preferredDok: 2,
        appropriateTiers: { min: 3, max: 10 },
        reason:
          'Tafelrooster verbindt rij x kolom met product — concept-versteviging voor recall.',
      },
      {
        type: 'step_solver',
        didacticRole: 'strategic',
        preferredDok: 3,
        appropriateTiers: { min: 7, max: 16 },
        reason:
          'Meerstaps-redenering bij contextopgaven; kind moet bewerking + volgorde verantwoorden.',
      },
      {
        type: 'strategy_solver',
        didacticRole: 'strategic',
        preferredDok: 3,
        appropriateTiers: { min: 7, max: 16 },
        reason:
          'Strategiekeuze expliciet maken (splitsen vs. compenseren vs. cijferend) — kern van handig rekenen op DOK 3.',
      },
      {
        type: 'number_sequence',
        didacticRole: 'concept',
        preferredDok: 2,
        appropriateTiers: { min: 5, max: 14 },
        reason:
          'Patroon-herkennen: bij handig keer/deel laat een reeks de sprong-grootte (×X / ÷X) of distributieve stap expliciet zien.',
      },
      {
        type: 'true_false_series',
        didacticRole: 'concept',
        preferredDok: 2,
        appropriateTiers: { min: 5, max: 14 },
        reason:
          'Oordelen over reeks beweringen: traint metacognitief herkennen van rekenfouten (tafelproducten, magnitude, bewerkingsvolgorde). Niet voor cijferend (verbergt procedure).',
      },
      {
        type: 'error_spotting',
        didacticRole: 'strategic',
        preferredDok: 3,
        appropriateTiers: { min: 7, max: 16 },
        reason:
          'Fout-in-uitwerking aanwijzen: vraagt om de bewerkings-volgorde of strategie kritisch te lezen — kerncompetentie voor VOLGORDE en GEMENGD.',
      },
      {
        type: 'estimation_slider',
        didacticRole: 'concept',
        preferredDok: 2,
        appropriateTiers: { min: 5, max: 14 },
        reason:
          'Schatten op slider: dwingt magnitude-besef ("tussen welke ronde getallen?") in plaats van exact uitrekenen.',
      },
      {
        type: 'ordering',
        didacticRole: 'strategic',
        preferredDok: 3,
        appropriateTiers: { min: 7, max: 16 },
        reason:
          'Volgorde-vragen: bij VOLGORDE de bewerkings-stappen ordenen; bij GEMENGD-TOETS sorteren op uitkomstgrootte.',
      },
    ],
    familyTypeHints: [
      {
        familyIdPattern: '^REK-TAFELS-',
        preferredTypes: [
          'fill_in',
          'multiple_choice',
          'multiplication_grid',
          'true_false_series',
        ],
        discouragedTypes: ['estimation_slider'],
        note: 'Tafels zijn recall: exact getal invullen of op rooster aanwijzen. true_false_series past voor "klopt 7×8 = 54?" oordelen-over-product. Geen schatting.',
      },
      {
        familyIdPattern: '^REK-CIJF-(PLUS|MIN)',
        preferredTypes: ['cijferend_rekenen', 'fill_in'],
        discouragedTypes: ['multiple_choice', 'true_false_series'],
        note: 'Cijferend +/− vereist kolomopzet met tussenresultaten — MC verbergt de procedure.',
      },
      {
        familyIdPattern: '^REK-CIJF-KEER',
        preferredTypes: ['cijferend_rekenen', 'fill_in', 'multiplication_grid'],
        discouragedTypes: ['multiple_choice'],
        note: 'Cijferend × vraagt expliciete tussenproducten — kolom of rooster, niet MC-keuze.',
      },
      {
        familyIdPattern: '^REK-CIJF-DEEL',
        preferredTypes: ['cijferend_delen', 'fill_in', 'step_solver'],
        discouragedTypes: ['multiple_choice', 'true_false_series'],
        note: 'Staartdeling: stap-voor-stap visualisatie — MC-vorm trivialiseert het deelproces.',
      },
      {
        familyIdPattern: '^REK-HANDIG-(KEER|DEEL)',
        preferredTypes: [
          'strategy_solver',
          'step_solver',
          'fill_in',
          'number_sequence',
        ],
        discouragedTypes: ['cijferend_rekenen', 'cijferend_delen'],
        note: 'Handig keer/deel draait om strategie-keuze (splitsen, verdubbelen, halveren). number_sequence laat de distributieve of multiplicatieve stap zien; cijferende vormen onderdrukken juist de handige strategie.',
      },
      {
        familyIdPattern: '^REK-HANDIG-PLUS-MIN',
        preferredTypes: [
          'strategy_solver',
          'step_solver',
          'fill_in',
          'true_false_series',
        ],
        discouragedTypes: ['cijferend_rekenen'],
        note: 'Handig plus/min: strategy_solver maakt splitsen/compenseren expliciet. true_false_series traint kritisch oordelen over een strategie-uitwerking (klopt deze sprong?).',
      },
      {
        familyIdPattern: '^REK-HANDIG-',
        preferredTypes: ['strategy_solver', 'step_solver', 'fill_in'],
        discouragedTypes: ['cijferend_rekenen', 'cijferend_delen'],
        note: 'Handig rekenen (overig): strategiekeuze centraal — strategy_solver of step_solver. Sub-families hierboven verfijnen.',
      },
      {
        familyIdPattern: '^REK-CONTEXT-',
        preferredTypes: [
          'step_solver',
          'bar_model_part',
          'fill_in',
          'multiple_choice',
          'true_false_series',
        ],
        note: 'Contextopgaven: bewerking herkennen + uitrekenen — strookmodel of stap-voor-stap; true_false_series past voor magnitude- of klopt-de-aanpak-oordelen.',
      },
      {
        familyIdPattern: '^REK-SCHATTEN',
        preferredTypes: ['estimation_slider', 'multiple_choice', 'fill_in'],
        discouragedTypes: ['true_false_series'],
        note: 'Schatten: slider of ronde-getal-keuze — exacte berekening is hier juist niet de bedoeling.',
      },
      {
        familyIdPattern: '^REK-VOLGORDE',
        preferredTypes: [
          'fill_in',
          'multiple_choice',
          'step_solver',
          'ordering',
          'error_spotting',
        ],
        discouragedTypes: ['estimation_slider'],
        note: 'Volgorde der bewerkingen: ordering maakt "welke stap eerst?" expliciet, error_spotting traint het herkennen van fouten in voorgerekende uitwerkingen. Geen schatting.',
      },
      {
        familyIdPattern: '^REK-GEMENGD-',
        preferredTypes: [
          'fill_in',
          'multiple_choice',
          'step_solver',
          'true_false_series',
          'error_spotting',
          'ordering',
        ],
        discouragedTypes: ['estimation_slider'],
        note: 'Gemengde toets: brede mix van vraagtypen om strategie-flexibiliteit te toetsen. Geen schatting (toets-context vraagt exacte uitkomst).',
      },
    ],
  },

  distractorTraps: [
    {
      id: 'borrow-skip',
      label: 'Lenen overslaan',
      description:
        'Bij aftrekken vergeten te lenen — komt voor bij cijferend aftrekken.',
    },
    {
      id: 'carry-skip',
      label: 'Onthouden overslaan',
      description:
        'Bij optellen of vermenigvuldigen vergeten de overdracht naar de volgende kolom mee te nemen.',
    },
    {
      id: 'table-neighbor',
      label: 'Buur-tafel',
      description:
        'Antwoord van de naburige tafel kiezen (7×8 vs 7×9 = 56 vs 63).',
    },
    {
      id: 'operation-swap',
      label: 'Bewerking verwisselen',
      description:
        'In contextopgave + ipv − of × ipv ÷ kiezen — meten of het kind de operatie herkent.',
    },
    {
      id: 'order-violation',
      label: 'Volgorde-fout',
      description:
        'Bij gemengde bewerkingen + vóór × of haakjes negeren: meet of "Meneer Van Dale" is geïnternaliseerd.',
    },
    {
      id: 'rounding-wrong-direction',
      label: 'Verkeerde afrond-richting',
      description:
        'Bij "minimaal nodig" naar beneden afronden (5,4 → 5) i.p.v. naar boven; bij "maximaal" andersom.',
    },
    {
      id: 'magnitude-error',
      label: 'Magnitude / grootte-orde-fout',
      description:
        'Antwoord een factor 10 (of meer) ernaast — komt door komma verkeerd plaatsen of een nul vergeten/extra.',
    },
    {
      id: 'step-forgotten',
      label: 'Stap vergeten',
      description:
        'Bij meerstaps-context één deelbewerking overgeslagen (bv. alleen prijs × aantal, geen verzendkosten).',
    },
    {
      id: 'wrong-scale',
      label: 'Verkeerde schaalfactor',
      description:
        'Bij verhoudings-context de factor verkeerd berekenen (14÷4 als 3 i.p.v. 3,5) — meet of het kind de verhouding precies maakt.',
    },
    {
      id: 'compensation-not-corrected',
      label: 'Compensatie niet teruggedraaid',
      description:
        'Bij handig rekenen +40 i.p.v. +39 gedaan, maar de −1 correctie vergeten — meet bewustzijn van compenseren-strategie.',
    },
    {
      id: 'distributive-half-applied',
      label: 'Distributief half toegepast',
      description:
        'Bij 8×27 = 8×25+8×2 alleen 8×25 gegeven (of alleen het laatste deel) — meet of het kind splitsen volhoudt.',
    },
    {
      id: 'context-wrong-data',
      label: 'Verkeerd getal uit context gekozen',
      description:
        'In contextopgave een irrelevant getal meegerekend (bv. begeleiders meegeteld voor entree).',
    },
  ],

  keyRules: [
    'Strategienaam ALTIJD benoemen in feedback (splitsen, compenseren, rijgen)',
    'Cijferend rekenen: tussenresultaten correct, lenen bij aftrekken',
    'Contextopgaven: bewerking herkennen is de kern, niet het uitrekenen — overweeg barModel als support',
    'Tafels-basis: puur recall, geen contextverhalen',
    'Distractoren in vergelijkbare grootte-orde: een magnitude-outlier (€41 vs €265) maakt elimineren-op-zicht mogelijk',
    'Trap-distractors: buur-tafel, lenen overslaan, bewerking verwisselen, verkeerde afrond-richting, stap vergeten',
    'Handig keer/deel: splitDiagram bij distributief splitsen, numberLineLeaps bij sprong-strategieën',
    'Bij "minimaal nodig" of "hoeveel passen er": altijd naar boven afronden — bewust in distractoren benutten',
  ],

  // GSR-25 (PR-9): per-module override copy migrated from inline
  // `moduleMessages` maps in didacticDepth.ts.
  qualityMessages: {
    explanationDepthRequirement:
      'REK explanation must show calculation steps or strategy name',
    distractorMisconceptionRequirement:
      'REK distractors should reference calculation or strategy error',
  },

  authoringGuideAnchor: '#2-rek--basisrekenen',
}
